import { useCallback, useMemo } from 'react';

import { type KnackCriteriaDateTimeValue } from '@/types/schema/KnackCriteria';
import {
  type SearchView,
  type SearchViewCriteriaOperator,
  type SearchViewFilterRule,
  type SearchViewFilters,
  type SearchViewInput
} from '@/types/schema/views/SearchView';
import { useCriteriaHelpers } from '@/hooks/helpers/useCriteriaHelpers';
import { isDateTimeRangeOperator, shouldHideValueBasedOnOperator } from '@/utils/field-operators';
import { getSanitizedTimeStringForCriteria } from '@/components/views/view-options/filters-inputs/date-time/DateTimeCriteriaValueTransformer';
import { useViewContext } from '@/components/views/ViewContext';
import { useViewQueryParamsContext } from '@/components/views/ViewQueryParamsContext';

function getSearchFilterCriteriaOperator(
  fieldInput: SearchViewInput,
  defaultOperator: SearchViewCriteriaOperator
): SearchViewCriteriaOperator {
  let operator: SearchViewCriteriaOperator = fieldInput.ignore_operators
    ? fieldInput.operator_default
    : defaultOperator;

  if (
    fieldInput.ignore_operators &&
    fieldInput.multi_type === 'many' &&
    fieldInput.multi_match === 'or'
  ) {
    operator = 'in';
  }

  if (
    fieldInput.ignore_operators &&
    fieldInput.multi_type === 'many' &&
    fieldInput.multi_match === 'and'
  ) {
    operator = 'is all';
  }

  return operator;
}

function getFilterRuleValue(rule: SearchViewFilterRule, fieldInput: SearchViewInput) {
  let value = shouldHideValueBasedOnOperator(rule.operator) ? '' : rule.value;
  // Some search fields have an array of values (e.g. connection fields) with empty values (e.g. [""])
  // as the default value. We need to filter out these empty values.
  // Also if the field is multi_type: 'one' we should only take the first value.
  if (Array.isArray(rule.value) && rule.value.length > 0) {
    value = rule.value.filter((val) => val !== '');
    value = fieldInput.ignore_operators && fieldInput.multi_type === 'one' ? value[0] : value;
  }
  // Email search input default from v3 builder can be an object with email property
  if (typeof rule.value === 'object' && (rule.value as Record<string, string>).email) {
    value = (rule.value as Record<string, string>).email;
  }
  // Person search input default from v3 builder can be an object with first and last properties
  if (
    typeof rule.value === 'object' &&
    (rule.value as Record<string, string>).first &&
    (rule.value as Record<string, string>).last
  ) {
    const defaultValue = rule.value as Record<string, string>;
    value = defaultValue.first ? defaultValue.first : '';
    value += defaultValue.last ? ` ${defaultValue.last}` : '';
  }
  return value;
}

export function useSearchViewFilters() {
  const { view, sourceTable } = useViewContext<SearchView>();
  const { actions, searchViewParams } = useViewQueryParamsContext();
  const { isCriteriaValueEmpty } = useCriteriaHelpers();

  const getSearchViewFilterRulesFromInputs = useCallback(
    (existingRulesFromParams?: SearchViewFilterRule[]) => {
      const filterRules: SearchViewFilterRule[] = [];

      // We want to keep track of the filters that have already been added to avoid using the same filter multiple times,
      // which can happen when the same field is used more than once as a search input
      const existingFiltersAddedByIndex = new Set<number>();

      view.groups.forEach((group) => {
        group.columns.forEach((column) => {
          column.fields.forEach((input) => {
            // Skip the input if it's static
            if (!input.field || input.field === 'section_break' || input.type === 'divider') {
              return;
            }

            const existingRule = existingRulesFromParams?.find((rule, index) => {
              // If the rule matches the input, we need to check that the rule has already been added. That way so we can skip it
              // and continue looking for another rule that matches the input
              if (rule.field === input.field && !existingFiltersAddedByIndex.has(index)) {
                existingFiltersAddedByIndex.add(index);
                return true;
              }
              return false;
            });

            const rule: SearchViewFilterRule = {
              inputId: input.id,
              field: existingRule?.field ?? input.field,
              operator: existingRule?.operator ?? input.operator,
              value: existingRule?.value ?? input.value,
              type: existingRule?.type ?? input.type,
              range: existingRule?.range ?? input.range
            };

            rule.operator = getSearchFilterCriteriaOperator(input, rule.operator);
            rule.value = getFilterRuleValue(rule, input);
            filterRules.push(rule);
          });
        });
      });

      return filterRules;
    },
    [view.groups]
  );

  const getSearchViewFieldInputsByFieldKey = useCallback(() => {
    const fieldInputs: Record<string, SearchViewInput> = {};
    view.groups.forEach((group) => {
      group.columns.forEach((column) => {
        column.fields.forEach((input) => {
          // Skip the input if it's static
          if (!input.field || input.field === 'section_break' || input.type === 'divider') {
            return;
          }
          fieldInputs[input.field] = input;
        });
      });
    });
    return fieldInputs;
  }, [view.groups]);

  const searchViewFilterRules = useMemo(
    () => getSearchViewFilterRulesFromInputs(searchViewParams.filters),
    [getSearchViewFilterRulesFromInputs, searchViewParams.filters]
  );

  const searchViewFieldInputsByFieldKey = useMemo(
    () => getSearchViewFieldInputsByFieldKey(),
    [getSearchViewFieldInputsByFieldKey]
  );

  const applySearchViewFilters = (newViewFilters: SearchViewFilters) => {
    const rules: SearchViewFilterRule[] = [];

    newViewFilters.rules.forEach((rule) => {
      if (rule.field === 'keyword_search' && rule.value) {
        rules.push({
          ...rule,
          operator: 'contains',
          value: rule.value
        });
        return;
      }

      // Ignore rules that use the "is any" operator, since they don't affect the search
      if (rule.operator === 'is any') {
        return;
      }

      const ruleField = [...sourceTable.fields, ...sourceTable.fieldsFromConnections].find(
        (field) => field.key === rule.field
      );

      if (!ruleField) {
        return;
      }

      // Create a new rule object that only has the base properties for a filter rule.
      // This is to ensure that we don't include properties like `range` or `type` that are not applicable to all fields.
      const ruleToAdd: SearchViewFilterRule = {
        field: rule.field,
        operator: rule.operator,
        value: rule.value
      };

      if (shouldHideValueBasedOnOperator(rule.operator)) {
        rules.push({
          ...ruleToAdd,
          value: undefined
        });
        return;
      }

      if (ruleField.type === 'connection' && Array.isArray(rule.value)) {
        rules.push({
          ...ruleToAdd,
          value: [rule.value[0]]
        });
        return;
      }

      if (ruleField.type === 'date_time') {
        if (isDateTimeRangeOperator(rule.operator)) {
          rules.push({
            field: rule.field,
            operator: rule.operator,
            range: rule.range,
            type: rule.type,
            value: undefined
          });
          return;
        }

        const ruleValue = rule.value as KnackCriteriaDateTimeValue;

        rules.push({
          field: rule.field,
          operator: rule.operator,

          // For date and time values, the server only expects `date` and `time` as strings
          value: {
            date: ruleValue.date,
            time: getSanitizedTimeStringForCriteria(ruleValue, ruleField.format)
          }
        });

        return;
      }

      // Avoid including rules with empty arrays as values since those can break the search
      if (Array.isArray(rule.value) && rule.value.length === 0) {
        return;
      }

      const input = searchViewFieldInputsByFieldKey[rule.field];

      // If the input can filter by value and the value is empty, we don't want to include it in the search
      if (!input.ignore_operators && isCriteriaValueEmpty(rule.value, ruleField)) {
        return;
      }

      rules.push({
        ...ruleToAdd,
        operator: getSearchFilterCriteriaOperator(input, rule.operator)
      });
    });

    // Remove `inputId` from the rules so it's not included in the URL params and in the request payload
    const rulesWithoutInputId = rules.map((rule) => {
      const { inputId, ...rest } = rule;
      return rest;
    });

    actions.setViewQueryParam({ filters: rulesWithoutInputId, page: undefined });
  };

  const resetSearchViewFilters = () => {
    actions.setViewQueryParam({ filters: undefined, page: undefined });
  };

  return {
    searchViewFilterRules,
    searchViewFieldInputsByFieldKey,
    applySearchViewFilters,
    resetSearchViewFilters,
    getSearchViewFilterRulesFromInputs
  };
}
